import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Heart, MapPin, Users, MessageSquare, Plane, Hospital } from "lucide-react";

export const Mission = () => {
  const missions = [
    {
      icon: Heart,
      title: "Reunite Tachen & Bettina",
      description: "After 26 years apart, bring together the refugee boy who couldn't walk and the young German social worker who cared for him. Their bond shaped both their lives forever.",
      color: "text-story-hope"
    },
    {
      icon: Hospital,
      title: "Return to Friedensdorf",
      description: "Visit the Peace Village in Oberhausen, Germany where their story began - the place that gave a disabled boy from Sudan the chance to walk again.",
      color: "text-story-peace"
    },
    {
      icon: Users,
      title: "Inspire Current Children",
      description: "Let Tachen speak to the children currently at the Peace Village, showing them that healing is possible and that someone who was once in their shoes is now thriving.",
      color: "text-story-sudan"
    },
    {
      icon: MapPin,
      title: "Document Their Journey",
      description: "Capture this extraordinary reunion and create a documentary that shows the lasting impact one person can have on another's life, inspiring others to help.",
      color: "text-story-germany"
    }
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-muted/30 to-background">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="bg-story-peace/10 text-story-peace border-story-peace/20 mb-4">
            <Plane className="w-4 h-4 mr-2" />
            THE MISSION
          </Badge>
          <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6">
            The Story That Started It All
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            In 1992, a 3-year-old disabled boy from Sudan met a young German social worker at Friedensdorf. 
            She helped him learn to walk. He helped her find her life's purpose. Now, 26 years later, 
            they've found each other again through Facebook. This is their story of reunion.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {missions.map((mission, index) => (
            <motion.div
              key={mission.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="h-full border-2 border-muted/50 hover:border-story-hope/30 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg bg-muted/50`}>
                      <mission.icon className={`w-6 h-6 ${mission.color}`} />
                    </div>
                    <CardTitle className="text-xl font-serif">{mission.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {mission.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <Card className="max-w-4xl mx-auto border-2 border-story-hope/20 bg-gradient-to-br from-story-hope/5 to-story-peace/5">
            <CardContent className="p-8">
              <MapPin className="w-12 h-12 text-story-hope mx-auto mb-4" />
              <h3 className="text-2xl font-serif font-semibold mb-4">From Facebook to Face-to-Face</h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                On January 22, 2020, after attending a trauma workshop, Bettina decided to search for "her lost boy" one more time. 
                There he was - Tachen's face on Facebook, hardly changed after 26 years. She knew immediately it was him. 
                Your support will help us bring together two people whose lives were forever intertwined by compassion, 
                creating a moment of healing that transcends borders and time.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};